import { Loader } from "@googlemaps/js-api-loader";
import {
  forwardRef,
  memo,
  ReactElement,
  useEffect,
  useRef,
  useState,
} from "react";
import { Loading, useTranslate } from "react-admin";
import ReactDOM from "react-dom";
import {
  ControlPosition,
  GoogleMapControls,
  MapBoundsChangedListener,
} from "./types";
import styles from "./ImperativeGoogleMap.module.css";
import { useTheme } from "@material-ui/styles";
import { alpha, Theme, Typography } from "@material-ui/core";
import { Error } from "@material-ui/icons";
import { useImperativeGoogleMap } from "./useImperativeGoogleMap";
import { useProLogsMapTheme } from "../../common/hooks/useProLogsMapTheme";

interface Props {
  loading?: boolean;
  error?: string;

  criticalError?: boolean;
  controls?: { [name: string]: ReactElement };
  controlPositions?: { [name: string]: ControlPosition };

  defaultMapType?: "roadmap" | "terrain" | "satellite" | "hybrid";
  defaultZoomLevel?: number;
  highlightedRoute?: Array<{ lat: number; lng: number }>;
  visitedRoute?: Array<{ lat: number; lng: number }>;

  onBoundsChanged?: MapBoundsChangedListener;
  zoomControl?: boolean;
  mapTypeControl?: boolean;
  scaleControl?: boolean;
  streetViewControl?: boolean;
  rotateControl?: boolean;
  fullscreenControl?: boolean;
  onMapLoaded?: (loaded: boolean) => void;
  clusteringEnabled?: boolean;
}

export const ImperativeGoogleMap = memo(
  forwardRef<GoogleMapControls, Props>((props, ref) => {
    const {
      loading,
      error,
      criticalError,
      defaultMapType,
      defaultZoomLevel,
      onBoundsChanged,
      fullscreenControl,
      mapTypeControl,
      rotateControl,
      scaleControl,
      streetViewControl,
      zoomControl,
      controls,
      controlPositions,
      onMapLoaded,
      highlightedRoute,
      visitedRoute,
      clusteringEnabled,
    } = props;

    const translate = useTranslate();
    const theme = useTheme<Theme>();
    const mapRef = useRef<HTMLDivElement>(null);
    const [map, setMap] = useState<google.maps.Map>();
    const zoomRef = useRef<number>(0);
    const boundsRef = useRef<{ lat: number; lng: number }>({
      lat: 0,
      lng: 0,
    });

    useImperativeGoogleMap(ref, theme, map, { clusteringEnabled });
    const googleMapStyles = useProLogsMapTheme();

    // Effect to load the map
    useEffect(() => {
      if (!mapRef.current) {
        return;
      }

      if (loading) {
        return;
      }

      if (error && criticalError) {
        return;
      }

      const loader = new Loader({
        id: "googlemap",
        apiKey: "AIzaSyCUBrZAf7Q4zF_Sd-KKtxvvvH3YdDcwlbE",
        version: "quarterly",
        language: "en",
        region: "US",
        libraries: ["drawing"],
      });

      loader
        .load()
        .then(() => {
          const loadedMap = new google.maps.Map(mapRef.current!, {
            center: { lat: 38, lng: -90 },
            mapTypeId: defaultMapType,
            zoom: defaultZoomLevel,
            disableDefaultUI: true,
            zoomControl: zoomControl,
            scaleControl: scaleControl,
            streetViewControl: streetViewControl,
            rotateControl: rotateControl,
            fullscreenControl: fullscreenControl,
            mapTypeControl: mapTypeControl,
            mapTypeControlOptions: mapTypeControl
              ? {
                  style: google.maps.MapTypeControlStyle.DEFAULT,
                  mapTypeIds: ["roadmap", "terrain", "satellite", "hybrid"],
                }
              : undefined,
          });
          if (onBoundsChanged) {
            boundsRef.current = loadedMap.getBounds()?.getCenter().toJSON() ?? {
              lat: 0,
              lng: 0,
            };
            zoomRef.current = loadedMap.getZoom() ?? 0;
            onBoundsChanged({
              southWest: loadedMap.getBounds()?.getSouthWest(),
              zoom: loadedMap.getZoom(),
              projection: loadedMap.getProjection(),
            });
          }

          google.maps.event.trigger(loadedMap, "resize");
          loadedMap.setCenter({ lat: 38, lng: -90 });
          setMap(loadedMap);
          if (onMapLoaded) {
            onMapLoaded(true);
          }
        })
        .catch((e) => console.error(e));
    }, [
      loading,
      error,
      criticalError,
      defaultMapType,
      defaultZoomLevel,
      onBoundsChanged,
      fullscreenControl,
      mapTypeControl,
      rotateControl,
      scaleControl,
      streetViewControl,
      zoomControl,
      onMapLoaded,
    ]);

    // Effect to subscribe to zoom changed and set the zoomChanged state
    useEffect(() => {
      if (!map) {
        return;
      }

      const zoomChangedListener = map.addListener("idle", () => {
        if (onBoundsChanged) {
          const zoom = map.getZoom() ?? 0;
          const bounds =
            map.getBounds()?.getCenter()?.toJSON() ??
            new google.maps.LatLng({ lat: 0, lng: 0 }).toJSON();
          if (
            zoomRef.current !== zoom ||
            bounds.lat !== boundsRef.current.lat ||
            bounds.lng !== boundsRef.current.lng
          ) {
            zoomRef.current = zoom;
            boundsRef.current = bounds;
            onBoundsChanged({
              southWest: map.getBounds()?.getSouthWest(),
              zoom: map.getZoom(),
              projection: map.getProjection(),
            });
          }
        }
      });

      return () => google.maps.event.removeListener(zoomChangedListener);
    });

    // Effect to add custom map controls
    useEffect(() => {
      if (!map) {
        return;
      }

      if (!controls) {
        return;
      }

      const controlNames = Object.getOwnPropertyNames(controls);
      for (const controlName of controlNames) {
        const controlPosition =
          (!!controlPositions ? controlPositions[controlName] : "TOP_CENTER") ??
          "TOP_CENTER";
        let controlDiv = map.controls[
          google.maps.ControlPosition[controlPosition]
        ]
          .getArray()
          .find((x) => (x as HTMLDivElement).id === controlName) as
          | HTMLDivElement
          | undefined;
        if (!controlDiv) {
          controlDiv = document.createElement("div");
          controlDiv.id = controlName;
          map.controls[google.maps.ControlPosition[controlPosition]].push(
            controlDiv
          );
        }

        ReactDOM.render(controls[controlName], controlDiv);
      }
    }, [map, controls, controlPositions]);

    // Effect to paint the map on theme change
    useEffect(() => {
      if (!map) {
        return;
      }

      map.setOptions({
        styles: googleMapStyles,
      });

      let transparentRoute: google.maps.Polyline;
      let solidRoute: google.maps.Polyline;
      if (highlightedRoute) {
        transparentRoute = new google.maps.Polyline({
          path: highlightedRoute,
          strokeColor: theme.palette.primary.main,
          strokeOpacity: 0.3,
          strokeWeight: 10,
        });
        transparentRoute.setMap(map);
      }

      if (visitedRoute) {
        solidRoute = new google.maps.Polyline({
          path: visitedRoute,
          strokeColor: theme.palette.primary.main,
          strokeOpacity: 0.8,
          strokeWeight: 10,
        });
        solidRoute.setMap(map);
      }

      return () => {
        transparentRoute?.setMap(null);
        solidRoute?.setMap(null);
      };
    }, [map, theme, highlightedRoute, visitedRoute, googleMapStyles]);

    if (loading) {
      return <Loading />;
    }

    return (
      <>
        <style type="text/css">
          {`
        .marker-label {
          font-weight: bold;
          background-color: ${alpha(theme.palette.background.paper, 0.6)};
          padding: 2px;
        }
        .margin-top-minus-20 {
          margin-top: -20px;
        }
        
        .margin-top-20 {
          margin-top: 20px;
        }
        `}
        </style>
        {error && criticalError && (
          <div className={styles["full-page-container"]}>
            <div style={{ color: theme.palette.error.main }}>
              <Error fontSize="small" />
              <Typography variant="body1">
                {translate(error, { _: error })}
              </Typography>
            </div>
          </div>
        )}
        {error && !criticalError && (
          <div
            className={styles["map-container-top-bar"]}
            style={{
              backgroundColor: theme.palette.error.main,
              color: theme.palette.getContrastText(theme.palette.error.main),
            }}
          >
            {error}
          </div>
        )}
        <div
          className={styles["map-container"]}
          ref={mapRef}
          style={{ display: error && criticalError ? "none" : "" }}
        ></div>
      </>
    );
  })
);
